import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RingLoader from 'react-spinners/RingLoader';
import { getContentLibrary } from '../../../services/aiComposer';
import { getUserAccessToken, getUserSession } from '../../../services/userSession';
import { resolveScreenHostContext } from '../../../services/resolveScreenHostContext';
import { hostRoleCanManageContent } from '../../../services/screenHostTeam';
import LibraryItemManageModal from '../../../components/ContentLibrary/LibraryItemManageModal';
import './ContentLibrary.css';
import '../../../components/ContentLibrary/LibraryItemManageModal.css';

export default function ContentLibrary() {
  const navigate = useNavigate();
  const token = getUserAccessToken();
  const session = getUserSession() || {};
  const { ownerUserId, role } = resolveScreenHostContext(session, token);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const canManageScreens = hostRoleCanManageContent(role);

  const load = useCallback(async () => {
    if (!token || !ownerUserId) return;
    setLoading(true);
    try {
      const res = await getContentLibrary(token, ownerUserId);
      setItems(res.data.items || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load library.');
    } finally {
      setLoading(false);
    }
  }, [ownerUserId, token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="contentLibrary">
      <div className="contentLibraryHeader">
        <div>
          <h1>Content Library</h1>
          <p>All your AI creations and uploads in one place.</p>
          <p className="contentLibraryHint">Click any item to add it to a screen or remove it.</p>
        </div>
        <button type="button" className="contentLibraryComposeBtn" onClick={() => navigate('/user/compose')}>
          + New with AI
        </button>
      </div>

      {loading && (
        <div className="contentLibraryLoader">
          <RingLoader color="#dc5024" size={40} />
        </div>
      )}

      {error && <p className="contentLibraryError">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="contentLibraryEmpty">
          <p>No content yet. Create your first design with AI.</p>
          <button type="button" onClick={() => navigate('/user/compose')}>
            Open AI Composer
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="contentLibraryGrid">
          {items.map((item) => (
            <button
              key={item.ItemId}
              type="button"
              className="contentLibraryCard contentLibraryCardButton"
              onClick={() => setSelectedItem(item)}
            >
              <div className="contentLibraryCardMedia">
                {item.MediaUrl?.endsWith('.mp4') ? (
                  <video src={item.MediaUrl} muted playsInline />
                ) : (
                  <img src={item.MediaUrl} alt={item.Title || 'Content'} />
                )}
              </div>
              <div className="contentLibraryCardInfo">
                <span className={`contentLibrarySource contentLibrarySource--${item.Source}`}>
                  {item.Source === 'ai' ? 'AI Generated' : 'Upload'}
                </span>
                <h3>{item.Title || 'Untitled'}</h3>
                <p>{new Date(item.CreatedAt).toLocaleDateString()}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <LibraryItemManageModal
        open={Boolean(selectedItem)}
        item={selectedItem}
        ownerUserId={ownerUserId}
        token={token}
        canManage={canManageScreens}
        onClose={() => setSelectedItem(null)}
        onChanged={load}
      />
    </div>
  );
}
